/**
 * ReplayPlayer — wave-3 item 2: full-viewport replay of a FINISHED
 * orchestrator run, driven by the real journal + session data through the
 * replayTimeline engine (./replayTimeline) on a virtual clock
 * (./useReplayClock).
 *
 * Entry contract: mounted ONLY by an explicit user gesture (the REPLAY button
 * on a terminal RecentRuns row). No auto-open, no demo mode, no fabricated
 * data — every timestamp and journal line rendered here comes from
 * fetchRun(runId) / fetchJournalTail(runId, 500).
 *
 * Unmistakable replay chrome (RED LINE — never mistakable for live):
 * a dashed 1px ACCENT border frames the entire viewport and a persistent
 * top banner reads 'REPLAY · {speed}x · real journal timestamps · {run_id}'
 * in DM Mono (the house font-mono token).
 *
 * prefers-reduced-motion: handled inside useReplayClock — the virtual clock
 * advances in discrete 1s steps instead of the 250ms cadence; nothing here
 * adds CSS transitions or rAF interpolation on top of it.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FleetJournalEntry,
  FleetRun,
  FleetSession,
  fetchJournalTail,
  fetchRun,
  fetchRuns,
} from '../../../services/fleetHudApi';
import {
  AssemblyLine,
  STAGE_AGENTS,
  stageFromSessions,
  ACCENT,
} from '../../fleet';
import type { ReactorCoreOverview } from '../../fleet';
import { MOOD_STYLE } from '../../fleet/fleetMood';
import BridgeCore from '../BridgeCore';
import { BELT_AGENTS } from '../agentOrbit';
import { parseTs } from '../../fleet/fleetTime';
import { itemBrief, missionName, missionSummary, taskForItem } from '../../fleet/missionBrief';
import { buildTimeline } from './replayTimeline';
import { useReplayClock } from './useReplayClock';

interface ReplayPlayerProps {
  /** The finished run to replay. */
  runId: string;
  /** Parent unmounts the overlay — called by Esc AND the banner close button. */
  onClose: () => void;
}

type LoadState = 'loading' | 'error' | 'ready';

/** Same canonical roster derivation as the live bridge (FleetHud) — the
 * constellation's slots must be identical in both rooms. */
const AGENT_ROSTER: readonly string[] = Object.freeze(
  Array.from(new Set([...Object.values(STAGE_AGENTS).flat(), ...BELT_AGENTS]))
);

/** Journal-terminal clock — same real-timestamp rendering as the HUD strip. */
function clockTime(ts?: string): string {
  const ms = parseTs(ts);
  if (ms === null) return '--:--:--';
  return new Date(ms).toLocaleTimeString('en-GB', { hour12: false });
}

/** 'T+{m}m {s}s' from a virtual-ms offset. */
function formatVirtualMs(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

export function ReplayPlayer({ runId, onClose }: ReplayPlayerProps) {
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [sessions, setSessions] = useState<FleetSession[]>([]);
  const [entries, setEntries] = useState<FleetJournalEntry[]>([]);
  // The run record (items_summary → PR links/commits, healed/rolled counts)
  // for the FINAL OUTCOME panel. Best-effort: the timeline never depends on
  // it, so its fetch failing degrades the panel to 'record unavailable'
  // rather than killing the replay.
  const [runDoc, setRunDoc] = useState<FleetRun | null>(null);
  // Poll-sweep phase anchor — pure chrome (the dashed REPLAY frame carries
  // the replay/live distinction); pinned at mount, the CSS animation spins
  // continuously on its own.
  const sweepAnchorRef = useRef<number>(Date.now());

  // Real data only — sessions + the 500-line journal tail of THIS run.
  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    Promise.all([fetchRun(runId), fetchJournalTail(runId, 500)])
      .then(([sess, journal]) => {
        if (cancelled) return;
        setSessions(Array.isArray(sess) ? sess : []);
        setEntries(Array.isArray(journal?.entries) ? journal.entries : []);
        setLoadState('ready');
      })
      .catch(() => {
        if (!cancelled) setLoadState('error');
      });
    fetchRuns(50)
      .then((runs) => {
        if (cancelled || !Array.isArray(runs)) return;
        setRunDoc(runs.find((r) => r.run_id === runId) ?? null);
      })
      .catch(() => {
        /* outcome panel degrades — never blocks the replay */
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  // Esc closes — same as the banner close button. Listener lives only while
  // the overlay is mounted.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const timeline = useMemo(
    () => (loadState === 'ready' ? buildTimeline(sessions, entries) : null),
    [loadState, sessions, entries]
  );

  // The hook is called unconditionally; before data lands the span is a
  // zero-length placeholder so the clock stays parked at 0.
  const span = timeline?.span ?? { start: 0, end: 0 };
  const clock = useReplayClock(span, timeline?.autoSpeed ?? 10);

  const state = timeline ? timeline.stateAt(clock.tVirtual) : null;
  const totalMs = Math.max(0, span.end - span.start);
  const elapsedMs = Math.min(totalMs, Math.max(0, clock.tVirtual - span.start));
  const fraction = totalMs > 0 ? elapsedMs / totalMs : 0;

  // Journal age on the virtual clock — feeds the AssemblyLine band readout.
  const lastVisibleEntry = state?.journalVisible[state.journalVisible.length - 1];
  const lastEntryMs = lastVisibleEntry ? parseTs(lastVisibleEntry.ts) : null;
  const journalAgeSeconds =
    lastEntryMs !== null ? Math.max(0, (clock.tVirtual - lastEntryMs) / 1000) : null;

  // Session HISTORY as-of-t: every session STARTED by tVirtual, completion
  // clamped to the virtual clock (still-open ones read as in-progress).
  // stateAt's sessionsVisible deliberately drops completed sessions — right
  // for mood, but it starved stageFromSessions and the assembly-line dots:
  // during an unmapped-agent stretch (fixer healing) the board had NO mapped
  // history left and went dark ('no agents?', owner 2026-08-14). Stage
  // consumers get history; NOW RUNNING filters it back down to open-at-t.
  const sessionsAsOf = useMemo(() => {
    const out: FleetSession[] = [];
    for (const s of sessions) {
      const start = parseTs(s.started_at);
      if (start === null || start > clock.tVirtual) continue;
      const end = parseTs(s.completed_at);
      out.push(end !== null && end <= clock.tVirtual ? s : { ...s, completed_at: null });
    }
    return out;
  }, [sessions, clock.tVirtual]);

  // NOW RUNNING at tVirtual — the open subset of the history slice.
  const activeSessions = useMemo(
    () => sessionsAsOf.filter((s) => s.completed_at === null || s.completed_at === undefined),
    [sessionsAsOf]
  );

  // ReactorCore view-model: stage from the HISTORY slice (holds the last
  // mapped stage through unmapped-agent stretches, same behavior as the live
  // board), elapsed measured on the VIRTUAL clock, never Date.now().
  const reactorOverview: ReactorCoreOverview | null = state
    ? {
        stage: stageFromSessions(sessionsAsOf) ?? '',
        items_summary: [],
        elapsed_s: elapsedMs / 1000,
        last_run_at: null,
      }
    : null;

  // RENDER-LAYER mood coercion (K3-consulted, 2026-08-14): deriveMood's
  // 'idle' means >90s of journal quiet with nothing open — real data, kept
  // honest in the engine — but a replayed run is alive at every playhead
  // moment, and at 100x the threshold trips in 0.9 wall-seconds, so ordinary
  // inter-agent gaps collapsed the core to a dead STANDBY/never ball
  // ('no glow, no agents', owner). The clock is clamped to the run's real
  // span, so mid-span quiet renders as 'waiting' (stage + glow held); the
  // engine's mood is untouched for any future quiet-band visuals.
  const replayMood =
    state && (state.mood === 'idle' || state.mood === 'down') ? 'waiting' : state?.mood ?? 'waiting';

  // The replay knows the future — quiet stretches count DOWN to the next
  // real journal event (both timestamps real; a countdown is suspense, an
  // elapsed counter is dead air). Null while an agent is in session.
  const nextEvent = useMemo(() => {
    if (activeSessions.length > 0) return null;
    let best: { inMs: number; message: string } | null = null;
    for (const e of entries) {
      const t = parseTs(e.ts);
      if (t === null || t <= clock.tVirtual) continue;
      if (!best || t - clock.tVirtual < best.inMs) {
        best = { inMs: t - clock.tVirtual, message: e.message ?? '' };
      }
    }
    return best;
  }, [activeSessions.length, entries, clock.tVirtual]);

  // Ghosted lookahead journal line — teleprompter, not invention: the next
  // REAL entry, unambiguously future-styled (▸ prefix, dimmed, T+ chip).
  const ghostEntry = useMemo(() => {
    let best: { t: number; entry: FleetJournalEntry } | null = null;
    for (const e of entries) {
      const t = parseTs(e.ts);
      if (t === null || t <= clock.tVirtual) continue;
      if (!best || t < best.t) best = { t, entry: e };
    }
    return best;
  }, [entries, clock.tVirtual]);

  // Tokens burned so far: sum over sessions COMPLETED by tVirtual (a session
  // reports its consumption only once finished — no fabricated mid-session
  // interpolation), plus the count for the '{n} sessions complete' line.
  // Derived from the RAW fetched sessions — stateAt deliberately hides
  // completed ones, so slicing must happen here.
  const { tokensBurned, sessionsDone } = useMemo(() => {
    let tokens = 0;
    let done = 0;
    for (const s of sessions) {
      const end = parseTs(s.completed_at);
      if (end !== null && end <= clock.tVirtual) {
        done += 1;
        if (typeof s.tokens_consumed === 'number') tokens += s.tokens_consumed;
      }
    }
    return { tokensBurned: tokens, sessionsDone: done };
  }, [sessions, clock.tVirtual]);

  const outcomeItems = runDoc?.items_summary ?? [];

  return (
    <div
      className={`force-dark fixed inset-0 z-[90] flex flex-col border border-dashed border-[${ACCENT}] bg-base text-text-primary`}
      style={{ colorScheme: 'dark', '--mood-glow': MOOD_STYLE[replayMood]?.glow } as React.CSSProperties}
      role="dialog"
      aria-label={`replay of run ${runId}`}
      data-testid="replay-player"
    >
      {/* Mood grade — the same room-tinting vignette the live BridgeShell
          carries, bound to the replay's coerced mood. Static CSS. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 60%, var(--mood-glow, transparent) 140%)',
          opacity: 0.22,
        }}
      />
      {/* Persistent replay banner — DM Mono, always visible, never subtle. */}
      <header className={`flex h-10 shrink-0 items-center justify-between gap-4 border-b border-[${ACCENT}] px-4`}>
        <p
          className={`truncate font-mono text-xs uppercase tracking-[0.15em] text-[${ACCENT}]`}
          data-testid="replay-banner"
        >
          REPLAY · {clock.speed}x · real journal timestamps · {runId}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close replay"
          className={`shrink-0 rounded-sm border border-[${ACCENT}] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[${ACCENT}] hover:bg-[${ACCENT}] hover:text-black`}
          data-testid="replay-close"
        >
          close ✕
        </button>
      </header>

      {loadState === 'loading' && (
        <div className="flex flex-1 items-center justify-center">
          <p className="font-mono text-xs text-text-muted" data-testid="replay-loading">
            loading replay…
          </p>
        </div>
      )}

      {loadState === 'error' && (
        <div className="flex flex-1 items-center justify-center px-6">
          {/* Non-dismissible: no close affordance on the message itself — the
              only way out is the banner close button / Esc. No fabricated
              journal is ever shown in its place. */}
          <p className="font-mono text-xs text-red-500" data-testid="replay-error">
            replay unavailable — could not load run {runId} (sessions or journal fetch failed)
          </p>
        </div>
      )}

      {loadState === 'ready' && state && reactorOverview && (
        <>
          {/* THE STAGE — fills the viewport, nothing below the fold. The old
              layout let the band's h-full swallow ~780px and shoved the
              terminal + core off-screen (the 'empty replay', 2026-08-14);
              shrink-0 wrappers + a height-bounded grid make that impossible
              by construction. */}
          <div className="flex min-h-0 flex-1 flex-col">
            {/* MISSION BANNER (K3): what this run set out to build, pinned
                above the band — the replay is the proof of the manifest.
                Name + description are operator-authored, verbatim. */}
            {runDoc && missionName(runDoc) && (
              <div
                className="flex shrink-0 items-baseline gap-3 overflow-hidden border-b border-hairline px-4 py-1.5"
                data-testid="replay-mission"
              >
                <span className="shrink-0 font-mono text-xs uppercase tracking-[0.15em] text-brand">
                  {missionName(runDoc)}
                </span>
                <span className="truncate font-mono text-[11px] text-text-muted">
                  {missionSummary(runDoc)}
                </span>
              </div>
            )}
            {/* (a) Assembly line — its own instance, band variant, fed from
                the time-sliced state at tVirtual. shrink-0 wrapper neutralizes
                the band's bridge-slot h-full. nowMs pins its elapsed readouts
                to the VIRTUAL clock. */}
            <div className="shrink-0">
              <AssemblyLine
                variant="band"
                activeRunId={runId}
                sessions={sessionsAsOf}
                items={outcomeItems}
                journalAgeSeconds={journalAgeSeconds}
                nowMs={clock.tVirtual}
              />
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 px-4 py-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,4fr)_minmax(0,3fr)]">
              {/* (b) Phosphor journal terminal — real entries, real
                  timestamps, sliced at tVirtual, newest pinned to the bottom
                  like a live tail. */}
              <section
                className="flex min-h-0 flex-col overflow-hidden rounded border border-hairline bg-base px-3 py-2 font-mono text-xs text-green-400"
                data-testid="replay-journal-terminal"
                aria-label="journal at replay time"
              >
                <p className="mb-1 shrink-0 text-[10px] uppercase tracking-wide text-text-muted">
                  journal · {runId} · T+{formatVirtualMs(elapsedMs)}
                </p>
                <div className="flex min-h-0 flex-1 flex-col justify-end overflow-hidden">
                  {state.journalVisible.length === 0 ? (
                    <p className="text-text-muted">no journal lines at this point in the run</p>
                  ) : (
                    state.journalVisible.slice(-21).map((l, i) => (
                      <p key={`${l.ts ?? i}|${i}`} className="truncate leading-5">
                        <span className="text-text-muted">{clockTime(l.ts)}</span>{' '}
                        <span className="uppercase">[{(l.level ?? 'info').toLowerCase()}]</span>{' '}
                        {l.message ?? ''}
                      </p>
                    ))
                  )}
                  {/* Ghosted LOOKAHEAD — the next real entry, future-styled:
                      ▸ prefix + dim + T+ chip. Teleprompter, never invention. */}
                  {ghostEntry && (
                    <p
                      className="truncate leading-5 opacity-40"
                      data-testid="replay-journal-ghost"
                    >
                      ▸ <span className="text-text-muted">T+{formatVirtualMs(ghostEntry.t - span.start)}</span>{' '}
                      {ghostEntry.entry.message ?? ''}
                    </p>
                  )}
                </div>
              </section>

              {/* (c) THE core — the same BridgeCore as the live bridge (glow,
                  constellation, readout), fed by replay time: stage/sessions
                  from the history slice, elapsed on the virtual clock
                  (virtualElapsed — a paused replay never creeps), mood
                  render-coerced (never STANDBY mid-run). Open center like the
                  live room — no boxed border. Quiet stretches add a countdown
                  to the next REAL event. */}
              <section className="flex min-h-0 flex-col items-center justify-center overflow-hidden">
                <BridgeCore
                  mood={replayMood}
                  overview={reactorOverview}
                  runId={runId}
                  alive={true}
                  roster={AGENT_ROSTER}
                  pollMs={8000}
                  polledAt={sweepAnchorRef.current}
                  sessions={sessionsAsOf}
                  virtualElapsed
                />
                {nextEvent && (
                  <p
                    className="mt-1 shrink-0 truncate font-mono text-[11px] text-text-muted"
                    data-testid="replay-next-event"
                  >
                    quiet · next event in {formatVirtualMs(nextEvent.inMs)} —{' '}
                    {nextEvent.message.slice(0, 60)}
                  </p>
                )}
              </section>

              {/* (d) Right column — what the machine was DOING and what came
                  of it. All real: time-sliced sessions, summed real token
                  counts, the run record's item outcomes. */}
              <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
                <section
                  className="shrink-0 rounded border border-hairline bg-surface px-3 py-2"
                  data-testid="replay-now-running"
                >
                  <p className="mb-1 font-mono text-[10px] uppercase tracking-wide text-text-muted">
                    now running · T+{formatVirtualMs(elapsedMs)}
                  </p>
                  {activeSessions.length === 0 ? (
                    <p className="font-mono text-xs text-text-muted">no agent in session</p>
                  ) : (
                    activeSessions.map((s, i) => {
                      const start = parseTs(s.started_at);
                      const inFor = start !== null ? formatVirtualMs(Math.max(0, clock.tVirtual - start)) : '—';
                      return (
                        <p key={s.session_id ?? i} className="truncate font-mono text-xs leading-5 text-text-primary">
                          <span className={`text-[${ACCENT}]`}>●</span> {s.agent_type ?? 'agent'}{' '}
                          <span className="text-text-muted">
                            · {s.model_used ?? '—'} · in session {inFor}
                          </span>
                        </p>
                      );
                    })
                  )}
                </section>

                <section
                  className="shrink-0 rounded border border-hairline bg-surface px-3 py-2"
                  data-testid="replay-tokens"
                >
                  <p className="mb-1 font-mono text-[10px] uppercase tracking-wide text-text-muted">
                    burn
                  </p>
                  <p className="font-mono text-xs text-text-primary">
                    <span className={`text-[${ACCENT}]`}>{tokensBurned.toLocaleString()}</span>{' '}
                    <span className="text-text-muted">tokens · {sessionsDone} session{sessionsDone === 1 ? '' : 's'} complete</span>
                  </p>
                </section>

                <section
                  className="min-h-0 rounded border border-hairline bg-surface px-3 py-2"
                  data-testid="replay-outcome"
                >
                  <p className="mb-1 font-mono text-[10px] uppercase tracking-wide text-text-muted">
                    final outcome{runDoc?.status ? ` · ${runDoc.status}` : ''}
                  </p>
                  {outcomeItems.length === 0 ? (
                    <p className="font-mono text-xs text-text-muted">
                      {runDoc ? 'no item summary on this run' : 'run record unavailable'}
                    </p>
                  ) : (
                    outcomeItems.map((it, i) => {
                      const brief = runDoc ? itemBrief(taskForItem(runDoc, it.item)) : '';
                      return (
                        <div key={it.item ?? i} className="font-mono text-xs leading-5">
                          <p className="truncate">
                            <span className="text-text-primary">{it.item ?? `item_${i + 1}`}</span>{' '}
                            <span className="text-text-muted">{it.status ?? '—'}</span>
                            {it.pr_url && (
                              <a
                                href={it.pr_url}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-1.5 text-brand hover:underline"
                              >
                                PR #{it.pr_url.split('/').pop()} ↗
                              </a>
                            )}
                            {it.commit && <span className="ml-1.5 text-text-muted">{it.commit.slice(0, 8)}</span>}
                          </p>
                          {/* Quoted brief — deterministic trim of the real task
                              spec (missionBrief), never a paraphrase. */}
                          {brief && <p className="truncate pl-3 text-text-muted">“{brief}”</p>}
                        </div>
                      );
                    })
                  )}
                  {runDoc && (
                    <p className="mt-1 font-mono text-[11px] text-text-muted">
                      {runDoc.items_healed ?? 0} healed · {runDoc.items_rolled_back ?? 0} rolled back ·{' '}
                      {Array.isArray(runDoc.escalation_reports) ? runDoc.escalation_reports.length : 0} esc
                    </p>
                  )}
                </section>
              </div>
            </div>
          </div>

          {/* Transport bar — play/pause, scrub (fraction of the real span),
              virtual elapsed readout, current speed. */}
          <footer
            className="flex h-12 shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-t border-hairline bg-surface px-4"
            data-testid="replay-transport"
          >
            <button
              type="button"
              onClick={clock.playing ? clock.pause : clock.play}
              aria-label={clock.playing ? 'Pause replay' : 'Play replay'}
              className={`rounded-sm border border-hairline px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-text-primary hover:border-[${ACCENT}] hover:text-[${ACCENT}]`}
              data-testid="replay-playpause"
            >
              {clock.playing ? 'pause' : 'play'}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.001}
              value={fraction}
              onChange={(e) => clock.seek(parseFloat(e.target.value))}
              aria-label="Scrub replay"
              className={`min-w-0 flex-1 accent-[${ACCENT}]`}
              data-testid="replay-scrub"
              disabled={totalMs === 0}
            />
            <span className="shrink-0 font-mono text-[11px] text-text-secondary" data-testid="replay-elapsed">
              T+{formatVirtualMs(elapsedMs)} of {formatVirtualMs(totalMs)}
            </span>
            <span className="shrink-0 font-mono text-[11px] text-text-muted" data-testid="replay-speed">
              {clock.speed}x
            </span>
          </footer>
        </>
      )}
    </div>
  );
}

export default ReplayPlayer;
